import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Check, AlertCircle } from 'lucide-react';
import { Button } from '../ui/Button';
import { supabase } from '../../lib/supabase';

interface AvatarUploadProps {
  currentAvatar: string;
  onAvatarChange: (avatarUrl: string) => void;
  userId: string;
  disabled?: boolean;
}

export const AvatarUpload: React.FC<AvatarUploadProps> = ({
  currentAvatar,
  onAvatarChange,
  userId,
  disabled = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const compressImage = (file: File, maxWidth: number = 400, quality: number = 0.8): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          quality
        );
      };

      img.src = URL.createObjectURL(file);
    });
  };

  const validateFile = (file: File): string | null => {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please select a valid image file (JPEG, PNG, WebP, or GIF)';
    }

    // Check file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return 'File size must be less than 5MB';
    }

    return null;
  };

  const uploadToSupabase = async (file: File): Promise<string> => {
    try {
      // Create unique filename
      const fileExt = file.name.split('.').pop() || 'jpg';
      const fileName = `${userId}-${Date.now()}.${fileExt}`;
      const filePath = `avatars/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('media')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('media')
        .getPublicUrl(data.path);

      return publicUrl;
    } catch (error) {
      console.error('Supabase upload failed:', error);
      throw error;
    }
  };

  const createDataUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    setSuccess(false);
    setUploading(true);

    try {
      // Validate file
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Compress image if it's larger than 400px
      const compressedFile = await compressImage(file);
      
      // Create preview
      const previewUrl = await createDataUrl(compressedFile);
      setPreview(previewUrl);

      let avatarUrl: string;

      try {
        // Try uploading to Supabase first
        avatarUrl = await uploadToSupabase(compressedFile);
      } catch (uploadError) {
        console.warn('Supabase upload failed, using data URL fallback:', uploadError);
        // Fallback to data URL
        avatarUrl = previewUrl;
      }

      // Update avatar
      onAvatarChange(avatarUrl);
      setSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);

    } catch (err) {
      console.error('Avatar upload error:', err);
      setError('Failed to upload avatar. Please try again.');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  const clearPreview = () => {
    setPreview(null);
    setError('');
    setSuccess(false);
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      {/* Avatar Display */}
      <div className="relative">
        <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-2 border-gray-200 bg-gray-100">
          <img 
            src={preview || currentAvatar} 
            alt="Profile" 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.src = 'https://images.pexels.com/photos/2253275/pexels-photo-2253275.jpeg?auto=compress&cs=tinysrgb&w=300';
            }}
          />
        </div>
        
        {/* Upload Button */}
        <button
          onClick={handleClick}
          disabled={disabled || uploading}
          className={`absolute bottom-0 right-0 p-2 rounded-full text-white transition-all duration-200 ${
            disabled || uploading
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-purple-600 hover:bg-purple-700 hover:scale-110 shadow-lg'
          }`}
          title={uploading ? 'Uploading...' : 'Change avatar'}
        >
          {uploading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : success ? (
            <Check className="h-4 w-4" />
          ) : (
            <Camera className="h-4 w-4" />
          )}
        </button>

        {/* Preview Controls */}
        {preview && !success && (
          <button
            onClick={clearPreview}
            className="absolute top-0 right-0 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            title="Cancel upload"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || uploading}
      />

      {/* Status Messages */}
      <div className="text-center space-y-2">
        {uploading && (
          <div className="flex items-center justify-center space-x-2 text-purple-600">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
            <span className="text-sm">Uploading avatar...</span>
          </div>
        )}

        {success && (
          <div className="flex items-center justify-center space-x-2 text-green-600">
            <Check className="h-4 w-4" />
            <span className="text-sm">Avatar updated successfully!</span>
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center space-x-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {!uploading && !error && !success && (
          <p className="text-xs text-gray-500">
            Click the camera icon to upload a new avatar
            <br />
            Supported formats: JPEG, PNG, WebP, GIF (max 5MB)
          </p>
        )}
      </div>

      {/* Advanced Upload Button */}
      {!preview && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleClick}
          disabled={disabled || uploading}
          className="flex items-center space-x-2"
        >
          <Upload className="h-4 w-4" />
          <span>Choose Photo</span>
        </Button>
      )}
    </div>
  );
};